import React, { useMemo, useState } from 'react';
import { useProducts } from '../useProducts';
import { useSiteSettings } from '../SiteSettingsContext';
import { SITE_SETTINGS_DEFAULTS } from '../siteSettingsDefaults';
import HomeHeroCarousel from './HomeHeroCarousel';
import { pageCopySection } from '../content/siteCopyHelpers';
import { HOME_COPY_DEFAULTS } from '../content/pageCopyDefaults';
import { brandNameFromSettings } from '../siteBrand';
import { heroSlidesWithUrls, resolveHeroSlides } from '../content/homeHeroSlides';
import HomeHeroCopyPanel from './HomeHeroCopyPanel';
import HomeEditorialHero from './HomeEditorialHero';

const TRUST_ICONS = ['spark', 'ship', 'pin'];

function TrustIcon({ type }) {
  if (type === 'ship') {
    return (
      <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M3 10h13v8H3zM16 12h2l3 3v3h-5v-6zM6 18a2 2 0 1 0 0 .01M18 18a2 2 0 1 0 0 .01" />
      </svg>
    );
  }
  if (type === 'pin') {
    return (
      <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    );
  }
  return (
    <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.8 5.7 21l2.3-7-6-4.6h7.6L12 2z" />
    </svg>
  );
}

/**
 * Home hero — editorial showcase (single photo) or compact split layout.
 */
export default function StorefrontHero({ className = '', compact = false }) {
  const { allProducts, loading: catalogLoading } = useProducts();
  const { settings: site } = useSiteSettings();
  const [imageReady, setImageReady] = useState(false);

  const homeCopy = useMemo(() => pageCopySection(site.page_copy, 'home'), [site.page_copy]);

  const heroConfig = useMemo(
    () =>
      resolveHeroSlides({
        homeCopy,
        seoOgImage: site.seo_home_og_image,
        allProducts,
        featuredIds: site.featured_products,
        catalogLoading,
      }),
    [homeCopy, site.seo_home_og_image, site.featured_products, allProducts, catalogLoading]
  );

  const slides = useMemo(() => heroSlidesWithUrls(heroConfig.slides), [heroConfig.slides]);
  const heroSlide = slides[0] || null;
  const editorial = !compact;

  const trustItems = useMemo(() => {
    if (compact) return [];
    const locFallback = site.footer_location || site.location_line || 'India';
    const labels = [
      homeCopy.hero_trust_1 || HOME_COPY_DEFAULTS.hero_trust_1,
      homeCopy.hero_trust_2 || HOME_COPY_DEFAULTS.hero_trust_2,
      String(homeCopy.hero_trust_3 || '').trim() || locFallback,
    ];
    return labels
      .map((label, i) => ({ key: `trust-${i}`, label, icon: TRUST_ICONS[i] }))
      .filter((item) => String(item.label || '').trim());
  }, [compact, homeCopy, site.footer_location, site.location_line]);

  const heroBadge = compact ? '' : homeCopy.hero_badge || HOME_COPY_DEFAULTS.hero_badge;
  const brandName = brandNameFromSettings(site);
  const title = site.hero_title || SITE_SETTINGS_DEFAULTS.hero_title;
  const subtitle = site.hero_subtitle || SITE_SETTINGS_DEFAULTS.hero_subtitle;
  const description = String(site.hero_description || SITE_SETTINGS_DEFAULTS.hero_description || '').trim();

  const copyPanel = (
    <HomeHeroCopyPanel
      compact={compact}
      brandName={brandName}
      title={title}
      subtitle={subtitle}
      description={description}
      trustItems={trustItems}
      trustIcon={TrustIcon}
      editorial={editorial}
    />
  );

  if (editorial) {
    return (
      <HomeEditorialHero
        slide={heroSlide}
        copyPanel={copyPanel}
        className={className}
        onImageReady={setImageReady}
      />
    );
  }

  const media = (
    <div className="home-hero__media-col">
      <div className="home-hero__media-stack">
        <span className="home-hero__media-shadow" aria-hidden="true" />
        <HomeHeroCarousel
          slides={heroSlide ? [heroSlide] : []}
          autoplaySeconds={0}
          badge={heroBadge}
          imageFit="cover"
          immersive={false}
          onSlideReady={setImageReady}
        />
      </div>
    </div>
  );

  return (
    <section
      className={`home-hero home-hero--split${compact ? ' home-hero--compact' : ''}${imageReady ? ' home-hero--ready' : ''} ${className}`.trim()}
      aria-label="Welcome"
    >
      <div className="home-hero__ambient" aria-hidden="true">
        <span className="home-hero__blob home-hero__blob--1" />
        <span className="home-hero__blob home-hero__blob--2" />
      </div>

      <div className="home-hero__shell">
        <div className="home-hero__split">
          <div className="home-hero__copy-col">{copyPanel}</div>
          {media}
        </div>
      </div>
    </section>
  );
}
